import { Form, Input, Radio, Space, Tree, TreeProps } from 'antd';
import { getTeamsWithRoles } from 'api/teams.api';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserStatusArray } from '@tools/ui/enums';
import { EnumsherlockUserStatus, SherlockUser, Team } from '@tools/ui/types/generated/graphql';
import useStateWhenMounted from '@tools/ui-hooks/useStateWhenMounted.hook';

type props = {
  onSubmit: (values: RequestFormValues) => void;
  onSubmitFailed: () => void;
  submitted?: boolean;
  selectedUser?: SherlockUser;
  isNewUserRequest?: boolean;
};

export type RequestFormValues = {
  email: string;
  roleIds: string[];
  status: EnumsherlockUserStatus;
};

const TeamRoleForm: React.FC<React.PropsWithChildren<props>> = observer(
  ({ onSubmit, onSubmitFailed, submitted, selectedUser, isNewUserRequest }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm<RequestFormValues>();
    const emptyForm =
      !form.getFieldValue('roleIds') && selectedUser && Object.keys(selectedUser).length !== 0;
    // TODO: getTeamsQuery via @apollo/client
    const [data, setData] = useStateWhenMounted<{ teamMany: Team[] }>();
    const [statusValue, setStatusValue] = useState<string | undefined>(selectedUser?.status);
    const fetchData = useCallback(async () => {
      const teamMany = await getTeamsWithRoles();
      setData({ teamMany });
    }, [setData]);

    useEffect(() => {
      fetchData();
    }, [fetchData]);

    useEffect(() => {
      if (selectedUser) {
        form.setFieldsValue({
          email: selectedUser.email,
          roleIds: selectedUser.roles as string[],
        });
      }
    }, [data, selectedUser, form]);

    const onCheck: TreeProps['onCheck'] = (checkedKeysValue) => {
      form.setFieldsValue({
        roleIds: checkedKeysValue as string[],
      });
    };

    useEffect(() => {
      if (submitted) {
        form.submit();
      }
    }, [submitted, form]);

    const teamsIDs = React.useMemo(() => data?.teamMany?.map((team) => team._id), [data?.teamMany]);

    const treeData = React.useMemo(
      () =>
        data?.teamMany.map((team) => {
          const childrens = team?.roles?.map((role) => {
            return { title: role.name, key: role._id };
          });
          return { title: team.name, key: team._id, children: childrens, checkable: false };
        }),
      [data?.teamMany],
    );

    if (emptyForm) {
      return null;
    }

    const checkedKeys = selectedUser?.roles as string[];

    const handleStatusChange = (e) => setStatusValue(e.target.value);

    return (
      <Form form={form} layout="vertical" onFinish={onSubmit} onFinishFailed={onSubmitFailed}>
        <Form.Item
          name="email"
          label={t('auth.request.email')}
          rules={[
            {
              required: true,
              pattern: /^[a-zA-Z0-9._%+-]+@((contractor\.)?melio\.com|xero\.com)$/i,
              message: t('auth.request.emailError'),
            },
          ]}
          data-testid="permission-request-error-message-email"
        >
          <Input data-testid="permission-request-input-email" />
        </Form.Item>
        {!isNewUserRequest && (
          <Form.Item
            name="status"
            label={t('auth.request.status')}
            initialValue={statusValue}
            rules={[
              {
                required: true,
                enum: UserStatusArray,
                message: t('auth.request.statusError'),
              },
            ]}
          >
            <Radio.Group onChange={handleStatusChange} value={statusValue}>
              <Space direction="vertical">
                {UserStatusArray.map((status) => (
                  <Radio key={status} value={status}>
                    {status}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </Form.Item>
        )}
        <Form.Item
          name="roleIds"
          label="role"
          rules={[{ required: true, message: t('auth.request.roleError') }]}
        >
          <Tree
            checkable
            treeData={treeData}
            defaultCheckedKeys={checkedKeys}
            expandedKeys={teamsIDs}
            onCheck={onCheck}
          />
        </Form.Item>
      </Form>
    );
  },
);

export default TeamRoleForm;
